import { useState } from 'react';
import { DEVISE, fmt } from '../lib/currency';
import { tr, trf } from '../lib/i18n';
import { margePiece } from '../lib/marge-piece';
import { C } from '../theme/palette';

/**
 * « 📈 Ta marge » d'une offre ou d'une facture du magasin : ce que tu vends,
 * ce que la marchandise t'a coûté et ton bénéfice, sans TVA ou avec TVA. La
 * carte ne s'affiche qu'à ceux qui voient le bénéfice ; le client ne la voit
 * jamais (elle n'est pas sur le document).
 *
 * @param {object} props propriétés
 * @param {object[]} props.lignes lignes de la pièce
 * @param {object[]} props.stock articles du stock
 * @param {number} [props.remisePct] remise globale de la pièce
 * @param {number} [props.tvaPct] taux du document
 * @returns {import('react').ReactElement|null} la carte
 */
export function MargePiece({ lignes, stock, remisePct, tvaPct }) {
  const [avecTva, setAvecTva] = useState(false);
  if (!(lignes || []).length) return null;
  const m = margePiece(lignes, stock, { remisePct, tvaPct });
  const vente = avecTva ? m.venteTtc : m.venteHt;
  const achat = avecTva ? m.achatTtc : m.achatHt;
  const benef = Math.round((vente - achat) * 100) / 100;
  const pct = vente > 0 ? Math.round((benef / vente) * 1000) / 10 : 0;
  const bouton = (actif) => ({
    border: 'none',
    padding: '5px 10px',
    fontSize: 9.5,
    fontWeight: 800,
    cursor: 'pointer',
    background: actif ? C.navy : '#fff',
    color: actif ? '#fff' : C.navy,
  });
  const ligne = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10.5,
    padding: '3px 0',
  };
  return (
    <div
      role="group"
      aria-label={tr('Ta marge')}
      style={{
        border: '1.5px solid ' + C.green,
        background: C.greenL,
        borderRadius: 12,
        padding: '11px 12px',
        marginTop: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 7,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 800, color: C.green }}>{tr('📈 Ta marge')}</span>
        <div
          style={{
            display: 'flex',
            border: '1.5px solid ' + C.navy,
            borderRadius: 99,
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            aria-pressed={!avecTva}
            onClick={() => setAvecTva(false)}
            style={bouton(!avecTva)}
          >
            {tr('Sans TVA')}
          </button>
          <button
            type="button"
            aria-pressed={avecTva}
            onClick={() => setAvecTva(true)}
            style={bouton(avecTva)}
          >
            {tr('Avec TVA')}
          </button>
        </div>
      </div>
      <div style={ligne}>
        <span style={{ color: C.grey }}>
          {avecTva ? tr('Tu vends (TTC, remises déduites)') : tr('Tu vends (HT, remises déduites)')}
        </span>
        <b>
          {DEVISE} {fmt(vente)}
        </b>
      </div>
      <div style={ligne}>
        <span style={{ color: C.grey }}>
          {avecTva ? tr('Tu as acheté (TTC, prix d’achat)') : tr('Tu as acheté (HT, prix d’achat)')}
        </span>
        <b>
          − {DEVISE} {fmt(achat)}
        </b>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 12,
          padding: '6px 0 0',
          marginTop: 3,
          borderTop: '1px solid ' + C.green + '44',
        }}
      >
        <b>{tr('Ton bénéfice')}</b>
        <b style={{ fontSize: 14, color: benef >= 0 ? C.green : C.red }}>
          {DEVISE} {fmt(benef)} · {pct} %
        </b>
      </div>
      {m.sansPrixAchat > 0 && (
        <div style={{ fontSize: 9.5, color: '#b45309', fontWeight: 700, marginTop: 5 }}>
          {trf(
            '⚠ {0} ligne(s) sans prix d’achat ({1}) : pas comptée(s) dans la marge.',
            m.sansPrixAchat,
            DEVISE + ' ' + fmt(m.venteSansPrixAchatHt),
          )}
        </div>
      )}
      <div style={{ fontSize: 9, color: C.grey, marginTop: 5, lineHeight: 1.4 }}>
        {tr(
          'Visible seulement par toi, jamais sur le document. Le vrai bénéfice est sans TVA : la TVA encaissée part à l’État, celle de tes achats t’est rendue.',
        )}
      </div>
    </div>
  );
}
